/*!
 * Copyright (c) 2015-2020 Cisco Systems, Inc. See LICENSE file.
 */

/* eslint camelcase: [0] */

import {MemoryStoreAdapter} from './lib/storage';
import CredentialsConfig from './credentials-config';

export default {
  maxAppLevelRedirects: 10,
  maxLocusRedirects: 5,
  maxAuthenticationReplays: 1,
  maxReconnectAttempts: 1,
  onBeforeLogout: [],
  trackingIdPrefix: 'webex-js-sdk',
  trackingIdSuffix: '',
  AlternateLogger: undefined,
  credentials: new CredentialsConfig(),
  /**
   * Explicitly selects the Webex environment. When omitted, the environment is inferred from the
   * U2C discovery URL.
   *
   * @type {'integration'|'production'|undefined}
   */
  environment: undefined,
  fedramp: process.env.ENABLE_FEDRAMP || false,
  services: {
    /**
     * When true, uses the user-onboarding service for user activation.
     * When false (default), uses the license service.
     *
     * @type {boolean}
     */
    useUserOnboardingServiceForActivations: false,

    /**
     * When true, requests the U2C catalog override.
     *
     * @type {boolean}
     */
    useCatalogOverride: false,

    /**
     * When true, skips fetching the preauth catalog during initialization
     * while the user is unauthenticated (no existing token). The catalog is
     * expected to be collected manually later instead. When false (default),
     * the preauth catalog is collected automatically during init.
     *
     * @type {boolean}
     */
    skipPreauthCatalogOnUnauthenticated: false,

    /**
     * Maximum time (in milliseconds) to wait for the initial service catalog
     * collection when `waitForCatalogInit` is enabled, before letting
     * `services.ready` (and therefore `webex.ready`) fire anyway. Prevents a
     * hung catalog request from leaving the app on a permanent spinner.
     *
     * @type {number}
     */
    catalogInitTimeout: 15000,

    /**
     * A list of services that are available prior to catalog collection.
     *
     * @type {Object}
     */
    discovery: {
      /**
       * The hydra discovery url.
       *
       * @type {string}
       */
      hydra: process.env.HYDRA_SERVICE_URL || 'https://api.ciscospark.com/v1',

      /**
       * load balanced region info url
       */
      sqdiscovery: process.env.SQDISCOVERY_SERVICE_URL || 'https://ds.ciscospark.com/v1/region',

      /**
       * The u2c discovery url
       *
       * @type {string}
       */
      u2c: process.env.U2C_SERVICE_URL || 'https://u2c.wbx2.com/u2c/api/v1',
    },

    /**
     * When true, considers all urls in `allowedDomains` as safe for auth tokens
     *
     * @type {boolean}
     */
    validateDomains: true,

    /**
     * When true, enables the CatalogUrlInterceptor which validates that all
     * outbound request URLs are in the service catalog or allowed domains.
     * This prevents SSRF attacks where attacker-controlled URLs could be
     * sent to the SDK.
     *
     * @type {boolean}
     */
    validateCatalogUrls: false,

    /**
     * services that don't need auth validation
     */

    servicesNotNeedValidation: ['webex-appapi-service'],

    /**
     * Contains a list of allowed domain host addresses.
     *
     * @type {Array<string>}
     */
    allowedDomains: [],
  },
  device: {
    preDiscoveryServices: {
      hydra: process.env.HYDRA_SERVICE_URL || 'https://api.ciscospark.com/v1',
      hydraServiceUrl: process.env.HYDRA_SERVICE_URL || 'https://api.ciscospark.com/v1',
    },
    validateDomains: true,
  },
  metrics: {
    type: ['behavioral', 'operational'],
  },
  calling: {
    /**
     * Controls whether U2C service catalogs should be cached and warmed from cache.
     * When false, the services layer will skip reading and writing the U2C cache.
     */
    cacheU2C: false,
  },
  payloadTransformer: {
    /**
     * When true, inbound payload transforms run at most once for the same successful response object.
     * This supports request flows that pass a response through more than one interceptor pipeline.
     */
    skipRepeatedInboundTransforms: false,
    predicates: [],
    transforms: [],
  },
  storage: {
    boundedAdapter: MemoryStoreAdapter,
    unboundedAdapter: MemoryStoreAdapter,
  },
};
